import React from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { cx } from '@emotion/css'
import { Box } from '../../../index'
import { Spinner } from 'front-end/src/components'
import { UserRow } from './UserRow'
import { EmptyMembersSelector } from './EmptyMembersSelector'
import { AddMemberRow } from './AddMemberRow'
import { GroupRow } from './GroupRow'
import { AUTOCOMPLETE_ITEM_HEIGHT, MIN_SEARCH_LEN } from '../constants'
import {
  IMembersSelectorGroup,
  IMembersSelectorUser
} from '../MembersSelectors.types'
import { useMembersSelectorContext } from '../MembersSelectorContext'
import { observer } from 'mobx-react-lite'

type MemberSelectorAutocompleteProps = {
  isSearching: boolean
}
export const MemberSelectorAutocomplete: React.FC<MemberSelectorAutocompleteProps> =
  observer(({ isSearching }) => {
    const {
      isMembersListShown,
      query,
      addGroup,
      addUser,
      autocompleteUsers,
      autocompleteGroups,
      isAddressBookActive,
      setShowMembersList
    } = useMembersSelectorContext()

    if (!isMembersListShown) {
      return null
    }

    const hasDataToShow =
      autocompleteUsers.length > 0 || autocompleteGroups.length > 0

    const renderListItem = ({ index, style }: ListChildComponentProps) => {
      const isUser = index >= autocompleteGroups.length
      const member = isUser
        ? autocompleteUsers[index - autocompleteGroups.length]
        : autocompleteGroups[index]

      return (
        <span style={style}>
          <AddMemberRow<unknown>
            member={member}
            key={member.id}
            addEntity={(isUser ? addUser : addGroup) as (e: unknown) => void}
            canBeAdded={!member.cantBeSelectedReason}
          >
            {isUser ? (
              <UserRow user={member as IMembersSelectorUser} action="add" />
            ) : (
              <GroupRow group={member as IMembersSelectorGroup} action="add" />
            )}
          </AddMemberRow>
        </span>
      )
    }

    return (
      <>
        <Box
          position="fixed"
          top={0}
          left={0}
          zIndex={1}
          boxSize="100%"
          onClick={() => setShowMembersList(false)}
        />
        <Box
          position="relative"
          zIndex={2}
          className={cx('MembersSelector__dropdown', {
            right: isAddressBookActive
          })}
        >
          {isSearching ? (
            <Spinner />
          ) : (
            <Box className="MembersSelector__dropdown-content">
              {hasDataToShow &&
              (query.length > MIN_SEARCH_LEN || isAddressBookActive) ? (
                <Box
                  className="MembersSelector__members"
                  h={
                    Math.max(
                      autocompleteGroups.length + autocompleteUsers.length,
                      1
                    ) * AUTOCOMPLETE_ITEM_HEIGHT
                  }
                >
                  <AutoSizer>
                    {({ height, width }) => (
                      <FixedSizeList
                        width={width}
                        height={height}
                        itemSize={AUTOCOMPLETE_ITEM_HEIGHT}
                        itemCount={
                          autocompleteGroups.length + autocompleteUsers.length
                        }
                      >
                        {renderListItem}
                      </FixedSizeList>
                    )}
                  </AutoSizer>
                </Box>
              ) : (
                <EmptyMembersSelector
                  query={query}
                  isAddressBookActive={isAddressBookActive}
                />
              )}
            </Box>
          )}
        </Box>
      </>
    )
  })
MemberSelectorAutocomplete.displayName = 'MemberSelectorAutocomplete'
